import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useAuth } from '../auth'
import { api, ApiError } from '../api'
import { useI18n, type Lang } from '../i18n'
import { useDisplayMode } from '../displayMode'
import type { DisplayMode, Supervisor } from '../types'
import { Spinner, useToast } from '../components/ui'

export default function Profile() {
  const { user, refresh } = useAuth()
  const { t, setLang } = useI18n()
  const { setMode } = useDisplayMode()
  const toast = useToast()
  const [supervisors, setSupervisors] = useState<Supervisor[]>([])
  const [loading, setLoading] = useState(true)
  const [name, setName] = useState(user?.name ?? '')
  const [supervisorId, setSupervisorId] = useState<string>(user?.supervisor_id ?? '')
  const [lang, setLangChoice] = useState<Lang>(user?.lang ?? 'fi')
  const [displayMode, setDisplayMode] = useState<DisplayMode>(user?.display_mode ?? 'normal')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      const { supervisors } = await api.supervisors()
      setSupervisors(supervisors)
    } catch {
      toast.push(t('profile.loadErr'), 'error')
    } finally {
      setLoading(false)
    }
  }, [toast, t])

  useEffect(() => {
    load()
  }, [load])

  // Keep local state aligned with the signed-in user once it resolves.
  useEffect(() => {
    if (user) {
      setName(user.name ?? '')
      setSupervisorId(user.supervisor_id ?? '')
      setLangChoice(user.lang ?? 'fi')
      setDisplayMode(user.display_mode ?? 'normal')
    }
  }, [user])

  async function save(e: FormEvent) {
    e.preventDefault()
    // Validate the optional password change client-side first.
    const wantsPassword = password.length > 0 || passwordConfirm.length > 0
    if (wantsPassword) {
      if (password.length < 8) {
        toast.push(t('profile.pwTooShort'), 'error')
        return
      }
      if (password !== passwordConfirm) {
        toast.push(t('profile.pwMismatch'), 'error')
        return
      }
    }
    setBusy(true)
    try {
      await api.updateProfile({
        name: name.trim(),
        supervisor_id: supervisorId || null,
        lang,
        display_mode: displayMode,
        ...(wantsPassword ? { password } : {}),
      })
      setLang(lang)
      setMode(displayMode)
      await refresh()
      setPassword('')
      setPasswordConfirm('')
      toast.push(t('profile.saved'), 'ok')
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : t('profile.saveErr'), 'error')
    } finally {
      setBusy(false)
    }
  }

  // Don't let a supervisor pick themselves.
  const options = supervisors.filter((s) => s.id !== user?.id)

  return (
    <div>
      <div className="page-head">
        <h1>{t('profile.title')}</h1>
        <p>{t('profile.subtitle')}</p>
      </div>

      <div className="card pad-lg" style={{ maxWidth: 520 }}>
        {loading ? (
          <div style={{ display: 'grid', placeItems: 'center', minHeight: 160 }}>
            <Spinner large />
          </div>
        ) : (
          <form className="auth-form" onSubmit={save}>
            <div className="field">
              <label>{t('profile.name')}</label>
              <input
                className="input"
                type="text"
                value={name}
                placeholder={t('profile.namePlaceholder')}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="field">
              <label>{t('profile.supervisor')}</label>
              <select className="input" value={supervisorId} onChange={(e) => setSupervisorId(e.target.value)}>
                <option value="">{t('profile.noSupervisor')}</option>
                {options.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name || s.email}
                  </option>
                ))}
              </select>
              <span className="muted" style={{ fontSize: 13 }}>{t('profile.supervisorHint')}</span>
            </div>
            <div className="field">
              <label>{t('profile.language')}</label>
              <select className="input" value={lang} onChange={(e) => setLangChoice(e.target.value as Lang)}>
                <option value="fi">Suomi</option>
                <option value="sv">Svenska</option>
              </select>
              <span className="muted" style={{ fontSize: 13 }}>{t('profile.languageHint')}</span>
            </div>
            <div className="field">
              <label>{t('mode.label')}</label>
              <select
                className="input"
                value={displayMode}
                onChange={(e) => setDisplayMode(e.target.value as DisplayMode)}
              >
                <option value="normal">{t('mode.normal')}</option>
                <option value="simple">{t('mode.large')}</option>
              </select>
              <span className="muted" style={{ fontSize: 13 }}>{t('mode.hint')}</span>
            </div>

            <div className="field">
              <label style={{ fontWeight: 700 }}>{t('profile.changePassword')}</label>
              <span className="muted" style={{ fontSize: 13 }}>{t('profile.passwordHint')}</span>
            </div>
            <div className="field">
              <label>{t('profile.passwordNew')}</label>
              <input
                className="input"
                type="password"
                value={password}
                autoComplete="new-password"
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="field">
              <label>{t('profile.passwordConfirm')}</label>
              <input
                className="input"
                type="password"
                value={passwordConfirm}
                autoComplete="new-password"
                onChange={(e) => setPasswordConfirm(e.target.value)}
              />
            </div>

            <button className="btn btn-primary btn-lg" type="submit" disabled={busy}>
              {busy ? <Spinner /> : t('profile.save')}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
